import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  CalendarPlus,
  CalendarX,
  CalendarDays,
  Mail,
  Download,
  ChevronLeft,
} from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { Skeleton } from '@shared/components/ui/skeleton';
import { useLanguage } from '@core/i18n';
import { verifyAttrs } from '@shared/verify/core/contract';
import BookingHeader from '@features/booking/components/BookingHeader';
import BookingFooter from '@features/booking/components/BookingFooter';
import { SummaryRow } from '@features/booking/components/SummaryRow';
import { BookingChrome } from '@features/booking/layouts';
import type { AddToCalendarData } from '../api/reservations';
import { amPmToHm, usDateToIso } from '../utils/calendarLinks';

const PRIMARY_CTA_CLASS =
  'bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md flex items-center justify-center gap-2';
const SECONDARY_CTA_CLASS =
  'bg-transparent border border-brand text-brand font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-muted flex items-center justify-center gap-2';

export type CalendarTarget = 'google' | 'outlook' | 'ics';
export type AddToCalendarBranch = 'loading' | 'ready' | 'not-found' | 'error';

export interface AddToCalendarViewProps {
  branch: AddToCalendarBranch;
  reservation: AddToCalendarData | null;
  /** Which option gets the gold slot. See `resolvePrimaryTarget` on the page. */
  primaryTarget: CalendarTarget;
  onSelect: (target: CalendarTarget) => void;
  onGoHome: () => void;
}

/**
 * Presentation for the add-to-calendar email link.
 *
 * Split from `AddToCalendarPage` so it is driven entirely by props: the page
 * owns the route param, the query and the link building, while everything the
 * guest sees is decided here.
 */
export function AddToCalendarView({
  branch,
  reservation,
  primaryTarget,
  onSelect,
  onGoHome,
}: AddToCalendarViewProps) {
  const { t } = useTranslation('booking');
  const { dateLocale } = useLanguage();

  const options: { target: CalendarTarget; label: string; icon: ReactElement }[] = [
    { target: 'google', label: 'Google Calendar', icon: <CalendarDays size={20} /> },
    { target: 'outlook', label: 'Outlook', icon: <Mail size={20} /> },
    {
      target: 'ics',
      label: t('ADD_TO_CALENDAR_APPLE_AND_OTHERS'),
      icon: <Download size={20} />,
    },
  ];
  const orderedOptions = [
    ...options.filter((o) => o.target === primaryTarget),
    ...options.filter((o) => o.target !== primaryTarget),
  ];

  const displayDate = reservation
    ? format(
        new Date(`${usDateToIso(reservation.dateFormatted)}T12:00:00`),
        'EEE, d MMM yyyy',
        { locale: dateLocale },
      )
    : '';

  return (
    <BookingChrome flexCol>
      <BookingHeader />

      <main
        className="booking-content pt-8 pb-10 flex flex-col items-center flex-1"
        {...verifyAttrs('AddToCalendarView', {
          branch,
          hasReservation: !!reservation,
          optionCount: branch === 'ready' ? orderedOptions.length : 0,
          primaryTarget: branch === 'ready' ? primaryTarget : undefined,
          partySize: reservation?.partySize,
        })}
      >
        {branch === 'loading' && <LoadingState />}

        {(branch === 'not-found' || branch === 'error') && (
          <div className="flex flex-col items-center max-w-md py-12 text-center">
            <CalendarX size={48} className="text-foreground/40 mb-6" />
            <h1 className="text-3xl sm:text-4xl font-display mb-2">
              {branch === 'error'
                ? t('ADD_TO_CALENDAR_ERROR_TITLE')
                : t('ADD_TO_CALENDAR_NOT_FOUND_TITLE')}
            </h1>
            <p className="text-sm text-foreground/50 mb-8">
              {branch === 'error'
                ? t('ADD_TO_CALENDAR_ERROR_SUBTITLE')
                : t('ADD_TO_CALENDAR_NOT_FOUND_SUBTITLE')}
            </p>
            <Button className={PRIMARY_CTA_CLASS} onClick={onGoHome}>
              {t('ADD_TO_CALENDAR_BACK_TO_HOME')}
            </Button>
          </div>
        )}

        {branch === 'ready' && reservation && (
          <>
            <div className="w-16 h-16 rounded-full bg-brand-muted flex items-center justify-center mb-6">
              <CalendarPlus size={32} className="text-brand" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-display mb-2 text-center">
              {t('ADD_TO_CALENDAR_TITLE_1')}
            </h1>
            <p className="text-sm text-foreground/50 mb-8 text-center">
              {t('ADD_TO_CALENDAR_TITLE_2')}
            </p>

            <div className="w-full max-w-sm px-4">
              <div className="bg-foreground/5 rounded-xl p-4 space-y-3">
                <SummaryRow
                  label={t('ADD_TO_CALENDAR_RESTAURANT')}
                  value={reservation.restaurantName}
                />
                <SummaryRow label={t('ADD_TO_CALENDAR_DATE')} value={displayDate} />
                <SummaryRow
                  label={t('ADD_TO_CALENDAR_TIME')}
                  value={amPmToHm(reservation.time)}
                />
                <SummaryRow
                  label={t('ADD_TO_CALENDAR_GUESTS')}
                  value={reservation.partySize}
                />
              </div>
            </div>

            <div className="w-full max-w-sm px-4 mt-8 space-y-3">
              {orderedOptions.map((option, index) => (
                <Button
                  key={option.target}
                  className={index === 0 ? PRIMARY_CTA_CLASS : SECONDARY_CTA_CLASS}
                  onClick={() => onSelect(option.target)}
                  data-verify-option={option.target}
                >
                  {option.icon}
                  {option.label}
                </Button>
              ))}
            </div>
          </>
        )}
      </main>

      <footer className="border-t border-border">
        <div className="flex items-center px-6 py-4">
          <button
            aria-label={t('ADD_TO_CALENDAR_BACK_TO_HOME')}
            className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm"
            onClick={onGoHome}
            type="button"
          >
            <ChevronLeft size={20} className="group-hover:scale-110 transition-transform" />
          </button>
        </div>
        <BookingFooter />
      </footer>
    </BookingChrome>
  );
}

function LoadingState() {
  return (
    <div className="w-full max-w-sm px-4 flex flex-col items-center">
      <Skeleton className="w-16 h-16 rounded-full mb-6" />
      <Skeleton className="h-9 w-64 mb-3" />
      <Skeleton className="h-4 w-44 mb-8" />
      <div className="w-full bg-foreground/5 rounded-xl p-4 space-y-3">
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-full" />
      </div>
      <div className="w-full mt-8 space-y-3">
        <Skeleton className="h-14 w-full rounded-xl" />
        <Skeleton className="h-14 w-full rounded-xl" />
        <Skeleton className="h-14 w-full rounded-xl" />
      </div>
    </div>
  );
}

export default AddToCalendarView;
